import { useState } from "react";
import LeadFormModal from "./LeadFormModal";
import { useScrollReveal } from "@/hooks/useScrollReveal";

const greenIncomeProducts = [
  { icon: "fas fa-spray-can", label: "All Purpose Cleaner" },
  { icon: "fas fa-tshirt", label: "Laundry Detergent" },
  { icon: "fas fa-hands-wash", label: "Hand Soap & Body Wash" },
  { icon: "fas fa-utensils", label: "Dish Soap & Pods" },
  { icon: "fas fa-capsules", label: "Vitamins & Supplements" },
  { icon: "fas fa-cookie-bite", label: "Snacks & Pantry Staples" },
  { icon: "fas fa-pump-soap", label: "Skincare & Personal Care" },
];

const greenSwapsProducts = [
  { icon: "fas fa-spray-can", label: "All-Purpose Cleaner" },
  { icon: "fas fa-tshirt", label: "Laundry Detergent" },
  { icon: "fas fa-hands-wash", label: "Hand Soap & Body Wash" },
  { icon: "fas fa-utensils", label: "Dish Soap & Pods" },
  { icon: "fas fa-capsules", label: "Vitamins & Supplements" },
  { icon: "fas fa-cookie-bite", label: "Snacks & Pantry Staples" },
  { icon: "fas fa-pump-soap", label: "Skincare & Personal Care" },
];

const greenSwapsBenefits = [
  "Safer for kids and pets",
  "Gentle on sensitive skin",
  "Better for the environment",
  "Cost-effective and concentrated",
];

const incomeSteps = [
  "Join the Members' Club: Become a member and replace everyday essentials with safer, greener products.",
  "Share with Your Circle: Connect with others looking for healthier options and share your link.",
  "Leverage Automation: Set up simple digital tools (landing pages, email sequences) to work for you.",
  "Earn When They Shop: Receive income whenever your referred customers make purchases.",
];

const incomeTimeline = [
  { period: "Weeks 1–2", desc: "Learning and Launching" },
  { period: "Weeks 2–3", desc: "Steady growth, covering small regular expenses" },
  { period: "Months 3–6", desc: "Potential to scale to part-time or full-time income" },
];

export default function PathsSection() {
  const [incomeModalOpen, setIncomeModalOpen] = useState(false);
  const [swapsModalOpen, setSwapsModalOpen] = useState(false);
  const { ref, visible } = useScrollReveal();

  return (
    <>
      <section id="paths" className="py-20 bg-olive-50">
        <div ref={ref} className={`mx-auto px-4 md:px-6 max-w-6xl transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-7'}`}>
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-olive-800 mb-3">
              Two Powerful Paths to Freedom & Wellness
            </h2>
            <p className="text-olive-600 text-base">
              Choose the path that fits your family's goals — or explore both!
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {/* Green Income Model */}
            <div className="bg-white rounded-2xl p-6 md:p-8 border border-olive-100 flex flex-col">
              <div className="w-12 h-12 flex items-center justify-center bg-olive-100 rounded-xl text-olive-500 text-2xl mb-5">
                <i className="fas fa-seedling"></i>
              </div>
              <h3 className="font-heading text-xl font-bold text-olive-800 mb-3">
                🌿 The Green Income Model
              </h3>
              <p className="text-olive-600 text-sm leading-relaxed mb-5">
                Simply share access to eco-friendly products families already need
                and want — no selling, no inventory, no pressure. Earn income when
                they shop.
              </p>
              <h4 className="text-xs font-bold text-olive-700 uppercase tracking-wider mb-3">
                How It Works:
              </h4>
              <ol className="space-y-2.5 mb-5 text-sm text-olive-700 list-none">
                {incomeSteps.map((step, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="shrink-0 w-5 h-5 mt-0.5 flex items-center justify-center bg-olive-100 text-olive-600 rounded-full text-xs font-bold">
                      {i + 1}
                    </span>
                    <span dangerouslySetInnerHTML={{ __html: step }} />
                  </li>
                ))}
              </ol>
              <h4 className="text-xs font-bold text-olive-700 uppercase tracking-wider mb-3">
                Example Products:
              </h4>
              <div className="grid grid-cols-2 gap-2.5 mb-5">
                {greenIncomeProducts.map((p, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 bg-olive-50 rounded-xl px-3 py-2 text-sm text-olive-700"
                  >
                    <span className="w-5 h-5 flex items-center justify-center text-olive-500 shrink-0">
                      <i className={`${p.icon} text-sm`}></i>
                    </span>
                    <span className="text-xs leading-tight">{p.label}</span>
                  </div>
                ))}
              </div>
              <h4 className="text-xs font-bold text-olive-700 uppercase tracking-wider mb-3">
                Income Timeline:
              </h4>
              <ul className="space-y-1.5 mb-6 text-sm text-olive-700">
                {incomeTimeline.map((t, i) => (
                  <li key={i}>
                    <strong>{t.period}:</strong> {t.desc}
                  </li>
                ))}
              </ul>
              <div className="mt-auto">
                <button
                  onClick={() => setIncomeModalOpen(true)}
                  className="whitespace-nowrap w-full bg-olive-500 hover:bg-olive-600 text-white font-semibold py-3 rounded-full text-sm transition-all hover:scale-105 cursor-pointer flex items-center justify-center gap-2"
                >
                  Learn How to Start Earning
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="fas fa-arrow-right text-xs"></i>
                  </span>
                </button>
              </div>
            </div>

            {/* 7 Easy Green Swaps */}
            <div className="bg-white rounded-2xl p-6 md:p-8 border border-olive-100 flex flex-col">
              <div className="w-12 h-12 flex items-center justify-center bg-olive-100 rounded-xl text-olive-500 text-2xl mb-5">
                <i className="fas fa-leaf"></i>
              </div>
              <h3 className="font-heading text-xl font-bold text-olive-800 mb-3">
                🌱 7 Easy Green Swaps
              </h3>
              <p className="text-olive-600 text-sm leading-relaxed mb-5">
                Small changes that make a big impact. Reduce toxins, save money,
                and create a healthier home environment for your family.
              </p>
              <h4 className="text-xs font-bold text-olive-700 uppercase tracking-wider mb-3">
                The 7 Swaps:
              </h4>
              <div className="grid grid-cols-2 gap-2.5 mb-5">
                {greenSwapsProducts.map((p, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 bg-olive-50 rounded-xl px-3 py-2 text-sm text-olive-700"
                  >
                    <span className="w-5 h-5 flex items-center justify-center text-olive-500 shrink-0">
                      <i className={`${p.icon} text-sm`}></i>
                    </span>
                    <span className="text-xs leading-tight">{p.label}</span>
                  </div>
                ))}
              </div>
              <h4 className="text-xs font-bold text-olive-700 uppercase tracking-wider mb-3">
                Key Benefits:
              </h4>
              <ul className="space-y-1.5 mb-6 text-sm text-olive-700">
                {greenSwapsBenefits.map((b, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <span className="w-4 h-4 flex items-center justify-center text-olive-500 shrink-0">
                      <i className="fas fa-check text-xs"></i>
                    </span>
                    {b}
                  </li>
                ))}
              </ul>
              <div className="mt-auto">
                <button
                  onClick={() => setSwapsModalOpen(true)}
                  className="whitespace-nowrap w-full bg-olive-500 hover:bg-olive-600 text-white font-semibold py-3 rounded-full text-sm transition-all hover:scale-105 cursor-pointer flex items-center justify-center gap-2"
                >
                  Get Your Free Green Swaps Guide
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="fas fa-arrow-right text-xs"></i>
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Green Income Modal */}
      <LeadFormModal
        isOpen={incomeModalOpen}
        onClose={() => setIncomeModalOpen(false)}
        title="Get The Green Income Guide"
        subtitle="Enter your info below and we'll send the guide straight to your inbox — plus a few bonus tips to get started!"
        pdfUrl="https://storage.readdy-site.link/project_files/eb47f83a-244f-4772-bff9-529b98c523b6/bf7ca6de-207b-41a1-ab1b-17d45ab623a0_Family-Side-Hustle-How-to-Earn-Extra-Income-Without-Selling.pptx.pdf?v=837b1e0ae1a261d529b2ddf005992be2"
        pdfFileName="Family-Side-Hustle-How-to-Earn-Extra-Income-Without-Selling.pdf"
        submitUrl="https://formspree.io/f/xjgjaval"
        formId="green-income-guide"
        icon="fas fa-seedling"
      />

      {/* Green Swaps Modal */}
      <LeadFormModal
        isOpen={swapsModalOpen}
        onClose={() => setSwapsModalOpen(false)}
        title="Get Your Free Green Swaps Guide"
        subtitle="Discover 7 easy swaps that reduce toxins and save money. Enter your info to get the guide delivered to your inbox!"
        pdfUrl="https://storage.readdy-site.link/project_files/eb47f83a-244f-4772-bff9-529b98c523b6/6eb3b5ec-cd3d-4932-9f64-6877fbdf2ba9_7-Easy-Green-Swaps-for-Busy-Families-Final-Branded.pptx.pdf?v=40fd700a34adf1f7c38b25a08c1bfb2e"
        pdfFileName="7-Easy-Green-Swaps-for-Busy-Families.pdf"
        submitUrl="https://formspree.io/f/xjgjaval"
        formId="green-swaps-guide"
        icon="fas fa-leaf"
      />
    </>
  );
}